import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  Car,
  TrendingUp,
  TrendingDown,
  MapPin,
  AlertCircle
} from 'lucide-react';
import { api } from '../../utils/api';
import StatsCard from './components/StatsCard';
import ChartCard from './components/ChartCard';
import RecentActivity from './components/RecentActivity';
import LiveOverview from './components/LiveOverview';
import DateRangePicker from '../../components/DateRangePicker';

const Dashboard = () => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date()
  });
  const [period, setPeriod] = useState('1d');
  const [selectedStatus, setSelectedStatus] = useState("trips"); // Track selected status for filtering
  const [developerFilter, setDeveloperFilter] = useState('exclude'); // 'exclude', 'include', 'only'
  const [regionalOffice, setRegionalOffice] = useState('');

  // Fetch regional offices for the selector
  const { data: regionalOfficesData } = useQuery(['regional-offices', 'list'], () =>
    api.get('/regional-offices', { params: { page: 1, limit: 100 } })
  );
  const regionalOffices = regionalOfficesData?.data?.data?.offices || [];

  // Fetch dashboard statistics
  const { data: statsData, isLoading: statsLoading, error: statsError } = useQuery(
    ['dashboard-stats', dateRange, period, developerFilter, regionalOffice],
    () => {
      const formatDate = (date) => {
        if (!date || isNaN(new Date(date).getTime())) {
          return '';
        }
        return new Date(date).toISOString().split('T')[0];
      };

      const startFormatted = formatDate(dateRange.startDate);
      const endFormatted = formatDate(dateRange.endDate);

      const params = {
        period,
        developerFilter,
        regionalOffice
      };

      if (startFormatted && endFormatted) {
        params.startDate = startFormatted;
        params.endDate = endFormatted;
      }

      return api.get('/overview/stats', { params });
    },
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      staleTime: 60000 // Consider data stale after 1 minute
    }
  );

  // Fetch real-time statistics
  const { data: realtimeData, isLoading: realtimeLoading } = useQuery(
    ['dashboard-realtime', developerFilter, regionalOffice],
    () => api.get('/overview/realtime', {
      params: { developerFilter, regionalOffice }
    }),
    {
      refetchInterval: 10000, // Refetch every 10 seconds
      staleTime: 5000 // Consider data stale after 5 seconds
    }
  );

  // Remove separate chart data fetching - use stats data instead

  if (statsError) {
    toast.error('Failed to load dashboard data');
  }

  const stats = statsData?.data?.data || {};

  // KPI rule configuration - single source of truth
  const KPI_RULES = {
    trips: { higherIsBetter: true },
    completed: { higherIsBetter: true },
    cancelled: { higherIsBetter: false },
    diverged: { higherIsBetter: false },
    failed: { higherIsBetter: false }
  };

  // Universal change calculator for all statistics
  const getChangeMeta = (current, previous, status) => {
    if (current == null || previous == null) {
      return { change: "—", changeType: "neutral", trend: "none" };
    }

    const higherIsBetter = KPI_RULES[status]?.higherIsBetter ?? true;

    const diff = current - previous;

    if (diff === 0) {
      return { change: "0%", changeType: "neutral", trend: "none" };
    }

    const percent = previous === 0 ? 100 : (diff / previous) * 100;

    const trend = diff > 0 ? "up" : "down";
    const isPositive = higherIsBetter ? diff > 0 : diff < 0;

    return {
      change: `${diff > 0 ? "+" : ""}${percent.toFixed(1)}%`,
      changeType: isPositive ? "positive" : "negative",
      trend
    };
  };

  // Filter chart data based on selected status
  // If selectedStatus === 'none' we intentionally return an empty dataset so the chart shows empty
  const getFilteredChartData = () => {
    if (selectedStatus === 'none') return [];
    if (!stats.trends?.daily) return [];

    // If the filter is 'trips' or falsy, return full daily dataset
    if (!selectedStatus || selectedStatus === 'trips') return stats.trends.daily || [];

    // For a specific status, map other status counts to zero so chart highlights only the chosen status
    return (stats.trends?.daily || []).map(item => ({
      ...item,
      completed: selectedStatus === 'completed' ? (item.completed || 0) : 0,
      cancelled: selectedStatus === 'cancelled' ? (item.cancelled || 0) : 0,
      diverged: selectedStatus === 'diverged' ? (item.diverged || 0) : 0,
      failed: selectedStatus === 'failed' ? (item.failed || 0) : 0,
      pending: selectedStatus === 'pending' ? (item.pending || 0) : 0,
      started: selectedStatus === 'started' ? (item.started || 0) : 0,
      pickedup: selectedStatus === 'pickedup' ? (item.pickedup || 0) : 0,
    }));
  };

  const statsCards = [
    {
      title: "Total Trips",
      value: stats.trips?.totalTrips ?? 0,
      prevValue: stats.trips?.prev?.totalTrips ?? 0,
      status: "trips",
      ...getChangeMeta(
        stats.trips?.totalTrips,
        stats.trips?.prev?.totalTrips,
        "trips"
      ),
      icon: Car,
      color: "primary"
    },
    {
      title: "Completed Trips",
      value: stats.trips?.completedTrips ?? 0,
      prevValue: stats.trips?.prev?.completedTrips ?? 0,
      status: "completed",
      ...getChangeMeta(
        stats.trips?.completedTrips,
        stats.trips?.prev?.completedTrips,
        "completed"
      ),
      icon: TrendingUp,
      color: "success"
    },
    {
      title: "Cancelled Trips",
      value: stats.trips?.cancelledTrips ?? 0,
      prevValue: stats.trips?.prev?.cancelledTrips ?? 0,
      status: "cancelled",
      ...getChangeMeta(
        stats.trips?.cancelledTrips,
        stats.trips?.prev?.cancelledTrips,
        "cancelled"
      ),
      icon: TrendingDown,
      color: "danger"
    },
    {
      title: "Diverged Trips",
      value: stats.trips?.divergedTrips ?? 0,
      prevValue: stats.trips?.prev?.divergedTrips ?? 0,
      status: "diverged",
      ...getChangeMeta(
        stats.trips?.divergedTrips,
        stats.trips?.prev?.divergedTrips,
        "diverged"
      ),
      icon: MapPin,
      color: "warning"
    },
    {
      title: "Unaccepted Trips",
      value: stats.trips?.failedTrips ?? 0,
      prevValue: stats.trips?.prev?.failedTrips ?? 0,
      status: "failed",
      ...getChangeMeta(
        stats.trips?.failedTrips,
        stats.trips?.prev?.failedTrips,
        "failed"
      ),
      icon: AlertCircle,
      color: "danger"
    }
  ];

  // const realtimeCards = [
  //   {
  //     title: 'Active Trips',
  //     value: realtime.activeTrips || 0,
  //     icon: Activity,
  //     color: 'primary'
  //   },
  //   {
  //     title: 'Online Drivers',
  //     value: realtime.onlineDrivers || 0,
  //     icon: Users,
  //     color: 'success'
  //   },
  //   {
  //     title: 'Recent Trips',
  //     value: realtime.recentTrips || 0,
  //     icon: Clock,
  //     color: 'warning'
  //   },
  //   {
  //     title: 'Recent Revenue',
  //     value: `₹${(realtime.recentRevenue || 0).toLocaleString()}`,
  //     icon: () => <span className="text-lg">₹</span>,
  //     color: 'info'
  //   }
  // ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            Overview of your ride-sharing platform
          </p>
        </div>
      </div>

      {/* Date Range Picker */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-2">
          {[
            { key: '1d', label: '1 Day' },
            { key: '7d', label: '7 Days' },
            { key: '15d', label: '15 Days' },
            { key: '1m', label: '1 Month' },
            { key: '2m', label: '2 Months' }
          ].map((p) => (
            <button
              key={p.key}
              onClick={() => {
                setPeriod(p.key);
                // Update date range to match the selected period
                const now = new Date();
                let startDate;
                switch (p.key) {
                  case '1d':
                    startDate = new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000);
                    break;
                  case '7d':
                    startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                    break;
                  case '30d':
                    startDate = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000);
                    break;
                  case '1m':
                    startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); // Approximate
                    break;
                  case '2m':
                    startDate = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000); // Approximate
                    break;
                  default:
                    startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                }
                setDateRange({ startDate, endDate: now });
              }}
              className={`px-3 py-1 text-sm rounded-md ${period === p.key
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className='flex flex-col-1 gap-6'>
          <div className="w-64 flex">
            <label className="form-label block text-sm font-medium text-gray-700 mb-1">Regional Office</label>
            <select
              aria-label="Regional Office"
              value={regionalOffice}
              onChange={(e) => setRegionalOffice(e.target.value)}
              className="block w-full text-sm px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Offices</option>
              {regionalOffices.map(office => (
                <option key={office._id} value={office._id}>{office.name}</option>
              ))}
            </select>
          </div>

          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={setDateRange}
          />
        </div>
      </div>

      
      {/* Developer Filter */}
      <div className="flex items-center space-x-4">
        <span className="text-sm font-medium text-gray-700">Developer Trips:</span>
        <div className="flex space-x-2">
          {[
            { key: 'exclude', label: 'Exclude' },
            { key: 'include', label: 'Include' },
            { key: 'only', label: 'Only Developer' }
          ].map((option) => (
            <button
              key={option.key}
              onClick={() => setDeveloperFilter(option.key)}
              className={`px-3 py-1 text-sm rounded-md ${developerFilter === option.key
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Live overview: drivers online, active drivers, trips right now and today */}
      <LiveOverview developerFilter={developerFilter} regionalOffice={regionalOffice} />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
        {statsCards.map((card, index) => (
          <div
            key={index}
            onClick={() => setSelectedStatus(card.status)}
            className={`cursor-pointer transition-all rounded-xl duration-200 ${selectedStatus === card.status
              ? 'ring-2 ring-primary-500 ring-offset-2'
              : 'hover:shadow-lg'
              }`}
          >
            <StatsCard
              {...card}
              loading={statsLoading}
            />
          </div>
        ))}
      </div>

      {/* Filter Reset Button (hide when Total Trips is selected) */}
      {selectedStatus && selectedStatus !== 'trips' && (
        <div className="flex justify-center">
          <button
            onClick={() => setSelectedStatus("trips")}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            Show All Statuses
          </button>
        </div>
      )}

      {/* Real-time Statistics */}
      {/* <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Real-time Activity</h3>
          <p className="text-sm text-gray-500">
            Live updates from your platform
          </p>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {realtimeCards.map((card, index) => (
              <StatsCard
                key={index}
                {...card}
                loading={realtimeLoading}
                compact
              />
            ))}
          </div>
        </div>
      </div> */}

      {/* Charts and Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trip Trends Chart */}
        <ChartCard
          title={"Trip Trends"}
          subtitle={selectedStatus ? `${selectedStatus} trips` : "Daily trip completion trends"}
          loading={statsLoading}
          // pass the full daily dataset and let ChartCard handle which series to show
          data={stats.trends?.daily ?? []}
          type="line"
          selectedStatus={selectedStatus}
        />

        {/* Revenue Chart */}
        <ChartCard
          title="Driver Revenue Trends"
          subtitle="Daily driver revenue trends"
          loading={statsLoading}
          data={stats.trends?.daily ?? []}
          type="bar"
        />
      </div>

      {/* Status Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Total Trips (replaces Trip Status Breakdown) */}
        <ChartCard
          title="Total Trips"
          subtitle="Daily total trips"
          loading={statsLoading}
          data={stats.trends?.daily ?? []}
          type="line"
          selectedStatus="trips"
          allowSeriesSelector={false}
        />

        {/* Payment Method Distribution */}
        <ChartCard
          title="Payment Methods"
          subtitle="Payment method usage"
          loading={statsLoading}
          data={stats.trends?.paymentDistribution ?? []}
          type="doughnut"
        />
      </div>

      {/* Recent Activity */}
      {/* <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
        </div>
        <div className="card-body">
          <RecentActivity />
        </div>
      </div> */}

      {/* Quick Actions */}
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="card-body text-center">
            <MapPin className="mx-auto h-12 w-12 text-primary-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Track Active Trips
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Monitor real-time trip progress
            </p>
            <button className="btn btn-primary">
              View Active Trips
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body text-center">
            <Users className="mx-auto h-12 w-12 text-success-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Manage Drivers
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Approve and manage driver accounts
            </p>
            <button className="btn btn-success">
              View Drivers
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-warning-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Support Queries
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Handle customer support requests
            </p>
            <button className="btn btn-warning">
              View Queries
            </button>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default Dashboard; 